import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { BellRing, Plus, Trash2, X, CheckCircle2 } from 'lucide-react';

const ALERT_KINDS = [
  { k: 'price_above',  label: 'Price above',        needs: ['threshold'] },
  { k: 'price_below',  label: 'Price below',        needs: ['threshold'] },
  { k: 'ema_cross',    label: 'EMA cross',          needs: ['fast', 'slow', 'direction'] },
  { k: 'rsi_above',    label: 'RSI overbought (>)', needs: ['threshold'] },
  { k: 'rsi_below',    label: 'RSI oversold (<)',   needs: ['threshold'] },
  { k: 'macd_cross',   label: 'MACD cross',         needs: ['direction'] },
  { k: 'volume_spike', label: 'Volume spike',       needs: ['multiplier'] },
  { k: 'atr_above',    label: 'ATR above',          needs: ['threshold'] },
];

/**
 * AlertsPanel — Phase 52.C.
 *
 * Create / list / delete alerts on the current symbol. Polls the
 * server every 12s for triggered notifications and fires a toast +
 * bumps `onBadgeChange(n)` so the Crypto tab can show a badge.
 */
export default function AlertsPanel({ symbol, onBadgeChange }) {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [kind, setKind] = useState('price_above');
  const [form, setForm] = useState({ threshold: '', fast: 20, slow: 50, direction: 'up', multiplier: 2.5 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/crypto/alerts');
      setItems(data?.items || []);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  // Poll for new triggers.
  useEffect(() => {
    let cancelled = false;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get('/crypto/alerts/poll');
        const notifs = data?.notifications || [];
        if (!cancelled && notifs.length) {
          for (const n of notifs) {
            toast.success(`🔔 ${n.symbol} · ${n.kind}`, {
              description: `Triggered at $${n.triggered_price?.toFixed?.(4) ?? n.triggered_price} — ${n.note || 'alert fired'}`,
            });
          }
          onBadgeChange?.((prev) => (prev || 0) + notifs.length);
          load();
        }
      } catch { /* noop */ }
    }, 12000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [onBadgeChange]);

  const create = async () => {
    setBusy(true);
    try {
      const spec = ALERT_KINDS.find(k => k.k === kind);
      const params = {};
      for (const need of spec.needs) {
        const v = form[need];
        params[need] = ['threshold', 'multiplier'].includes(need) ? parseFloat(v) : (['fast', 'slow'].includes(need) ? parseInt(v) : v);
      }
      const body = { kind, symbol, params, note: `${spec.label} on ${symbol}` };
      const { data } = await api.post('/crypto/alerts', body);
      setItems((it) => [data, ...it]);
      setCreating(false);
      toast.success(`Alert created for ${symbol}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const remove = async (aid) => {
    setBusy(true);
    try {
      await api.delete(`/crypto/alerts/${aid}`);
      setItems((it) => it.filter(a => a.id !== aid));
    } catch { toast.error('Delete failed'); }
    finally { setBusy(false); }
  };

  const symbolAlerts = useMemo(() => items.filter(a => a.symbol === symbol), [items, symbol]);
  const otherAlerts  = useMemo(() => items.filter(a => a.symbol !== symbol), [items, symbol]);

  const spec = ALERT_KINDS.find(k => k.k === kind);

  return (
    <div className="border border-[var(--border-default)] bg-[var(--bg-panel)] flex flex-col" data-testid="crypto-alerts-panel">
      <div className="flex items-center justify-between border-b border-[var(--border-default)] px-2 py-1.5">
        <div className="text-[10px] tracking-[0.35em] uppercase text-cyan-sys flex items-center gap-1.5">
          <BellRing size={11} /> // alerts ({items.length})
        </div>
        <button onClick={() => setCreating(v => !v)}
                className="text-[9px] tracking-widest uppercase text-cyan-sys hover:underline flex items-center gap-1"
                data-testid="crypto-alerts-toggle">
          {creating ? <X size={10} /> : <Plus size={10} />} {creating ? 'cancel' : 'new'}
        </button>
      </div>

      {creating && (
        <div className="p-2 border-b border-[var(--border-default)] flex flex-col gap-1.5" data-testid="crypto-alert-form">
          <select
            value={kind} onChange={(e) => setKind(e.target.value)}
            className="bg-black border border-[var(--border-default)] focus:border-cyan-sys outline-none px-2 py-1 text-[11px] font-mono text-cyan-sys"
            data-testid="crypto-alert-kind"
          >
            {ALERT_KINDS.map(k => <option key={k.k} value={k.k}>{k.label}</option>)}
          </select>
          {spec.needs.includes('threshold') && (
            <input type="number" step="0.0001" placeholder="threshold"
                   data-testid="crypto-alert-threshold"
                   value={form.threshold}
                   onChange={(e) => setForm({ ...form, threshold: e.target.value })}
                   className="bg-black border border-[var(--border-default)] outline-none px-2 py-1 text-[11px] font-mono" />
          )}
          {spec.needs.includes('fast') && (
            <div className="grid grid-cols-2 gap-1.5">
              <input type="number" placeholder="fast" value={form.fast}
                     onChange={(e) => setForm({ ...form, fast: e.target.value })}
                     className="bg-black border border-[var(--border-default)] outline-none px-2 py-1 text-[11px] font-mono" />
              <input type="number" placeholder="slow" value={form.slow}
                     onChange={(e) => setForm({ ...form, slow: e.target.value })}
                     className="bg-black border border-[var(--border-default)] outline-none px-2 py-1 text-[11px] font-mono" />
            </div>
          )}
          {spec.needs.includes('direction') && (
            <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}
                    className="bg-black border border-[var(--border-default)] outline-none px-2 py-1 text-[11px] font-mono">
              <option value="up">up (bullish cross)</option>
              <option value="down">down (bearish cross)</option>
            </select>
          )}
          {spec.needs.includes('multiplier') && (
            <input type="number" step="0.1" placeholder="volume × avg"
                   value={form.multiplier}
                   onChange={(e) => setForm({ ...form, multiplier: e.target.value })}
                   className="bg-black border border-[var(--border-default)] outline-none px-2 py-1 text-[11px] font-mono" />
          )}
          <button onClick={create} disabled={busy}
                  className="border border-cyan-sys text-cyan-sys px-2 py-1 text-[9px] tracking-widest hover:bg-cyan-sys hover:text-black disabled:opacity-40"
                  data-testid="crypto-alert-submit">
            create alert for {symbol}
          </button>
        </div>
      )}

      <div className="max-h-56 overflow-y-auto">
        {items.length === 0 && (
          <div className="p-3 text-[10px] text-[var(--txt-muted)]">
            No alerts yet. Hit <span className="text-cyan-sys">+ new</span> to create your first.
          </div>
        )}
        {[...symbolAlerts, ...otherAlerts].map(a => (
          <div key={a.id} className="grid grid-cols-[1fr_auto] gap-2 px-2 py-1.5 border-b border-[var(--border-default)] last:border-b-0"
               data-testid={`crypto-alert-row-${a.id}`}>
            <div className="min-w-0">
              <div className="text-[11px] font-mono text-[var(--txt-primary)] flex items-center gap-1">
                {a.status === 'triggered' && <CheckCircle2 size={10} className="text-buy" />}
                {a.symbol}
                <span className="text-[9px] tracking-widest uppercase text-[var(--txt-muted)]">· {a.kind}</span>
              </div>
              <div className="text-[9px] text-[var(--txt-muted)] font-mono truncate">
                {JSON.stringify(a.params).replace(/[{}"]/g, '').replace(/,/g, ' · ')}
              </div>
              {a.status === 'triggered' && a.triggered_price != null && (
                <div className="text-[9px] text-buy font-mono">fired @ ${Number(a.triggered_price).toFixed(4)}</div>
              )}
            </div>
            <button onClick={() => remove(a.id)}
                    className="text-[var(--txt-muted)] hover:text-sell"
                    data-testid={`crypto-alert-delete-${a.id}`}>
              <Trash2 size={11} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
